// ============================================================================
//  VisibilityNotice.jsx — the one-time "your team can see this" notice.
//
//  Shown once per man, at his first login after team visibility goes live.
//  This is a consent notice, so acknowledgement is recorded on his profile
//  rather than in localStorage: it survives a new phone, and there is a record
//  that every man actually saw it.
//
//  Deliberately NOT dismissible by clicking the backdrop or an X. The only way
//  out is the acknowledge button, so nobody can wave it away without reading.
//  It is also not a substitute for saying this out loud at a meeting.
// ============================================================================

import React, { useState } from "react";
import { Users, Eye, Lock, ShieldCheck } from "lucide-react";
import { C, display, kicker, overlay, sheet, primaryBtn } from "../theme/tokens.js";

export default function VisibilityNotice({ onAcknowledge }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function accept() {
    setBusy(true);
    setErr("");
    try {
      await onAcknowledge();
    } catch (e) {
      setErr(e?.message || "Could not save that. Check your connection and try again.");
      setBusy(false);
    }
  }

  const Point = ({ icon: Icon, title, children }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, background: C.redSoft,
        display: "grid", placeItems: "center", marginTop: 1 }}>
        <Icon size={15} color={C.red} />
      </div>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );

  return (
    <div style={{ ...overlay, zIndex: 200 }}>
      <div style={{ ...sheet, maxWidth: 520 }}>
        <div style={kicker}>Before you start</div>
        <h2 style={{ ...display, fontSize: 26, margin: "8px 0 10px", color: C.text }}>
          Your team walks with you
        </h2>
        <p style={{ fontSize: 14, color: C.sub, lineHeight: 1.65, margin: "0 0 20px" }}>
          Mile Marker is built for brothers who hold each other accountable. That
          only works if everyone knows exactly who sees what, so here it is plainly.
        </p>

        <div style={{ display: "grid", gap: 16 }}>
          <Point icon={Eye} title="Your team can see your updates">
            Every man on your team sees your goals, your percentages, your status,
            and what you write each month - including your blockers. Your Team
            Admin sees the same.
          </Point>
          <Point icon={Users} title="That is the point, not the price">
            A struggle named out loud is a struggle the group can walk into with
            you. Write your blockers as though a brother will read them, because
            he will.
          </Point>
          <Point icon={Lock} title="You can keep a goal's details private">
            If something is not ready to share, open that goal and turn on
            <b style={{ color: C.text }}> Keep my updates private</b>. Your notes,
            blockers and photos on it stay between you and your Team Admin. The
            goal itself still shows on your card, so the group can still walk with
            you on it.
          </Point>
          <Point icon={ShieldCheck} title="Only you can change your goals">
            Everyone else has read access. Nobody can edit your goals or log a
            Mile Marker for you.
          </Point>
        </div>

        {err && (
          <div style={{ marginTop: 16, fontSize: 13, color: C.redDk }}>{err}</div>
        )}

        <button onClick={accept} disabled={busy}
          style={{ ...primaryBtn, width: "100%", marginTop: 24, justifyContent: "center",
            padding: "12px 18px", fontSize: 14, opacity: busy ? 0.7 : 1,
            cursor: busy ? "default" : "pointer" }}>
          {busy ? "…" : "I understand - let's go"}
        </button>

        <div style={{ marginTop: 12, fontSize: 11.5, color: C.faint, textAlign: "center",
          lineHeight: 1.5 }}>
          You can read this again any time under Help.
        </div>
      </div>
    </div>
  );
}
